import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Upload, Clock, XCircle, ShieldCheck, ArrowRight, ExternalLink, UserPlus, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { MKT_CSS, MktHeader } from "../marketplace/MktShared";
import { LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5"><Label>{label}</Label>{children}{hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}</div>
);

function FileBtn({ onDone, testid, accept = "image/*", label = "Upload" }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        setBusy(true);
        try {
          const fd = new FormData(); fd.append("file", f);
          const { data } = await api.post("/uploads", fd);
          onDone(data.url || data.path || ""); toast.success("Uploaded");
        } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
      }} />
      <Button type="button" variant="outline" size="sm" data-testid={testid} onClick={() => ref.current?.click()} disabled={busy} className="rounded-full font-bold">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}{label}
      </Button>
    </>
  );
}

const PendingScreen = () => (
  <div className="min-h-screen bg-background"><style>{MKT_CSS}</style><MktHeader />
    <div className="max-w-xl mx-auto px-4 py-16 text-center space-y-5" data-testid="apply-pending">
      <div className="mx-auto h-16 w-16 rounded-full bg-amber-500/10 flex items-center justify-center"><Clock className="h-8 w-8 text-amber-500" /></div>
      <h1 className="font-display font-extrabold text-3xl tracking-tight">Application Under Review</h1>
      <div className="mkt-card p-5 text-left space-y-2 text-sm">
        <p className="flex justify-between"><span className="text-muted-foreground">Payment Verification</span><span className="font-bold text-amber-600">PENDING</span></p>
        <p className="flex justify-between"><span className="text-muted-foreground">Freelancer Application</span><span className="font-bold text-amber-600">PENDING APPROVAL</span></p>
      </div>
      <p className="text-sm text-muted-foreground">MILTROO admin will verify your payment and approve your freelancer account. After approval you can complete your professional profile (skills, experience, portfolio, payout details) and start selling.</p>
      <Button onClick={() => (window.location.href = "/marketplace")} data-testid="apply-pending-home" className="rounded-full font-bold gradient-bg text-white border-0">Back to Marketplace</Button>
    </div>
  </div>
);

const PayBlock = ({ link, price, pay, setPay, testidPrefix }) => (
  <>
    {link
      ? <a href={link} target="_blank" rel="noreferrer" data-testid={`${testidPrefix}-pay-link`}
          className="flex items-center justify-center gap-2 h-11 rounded-full font-bold text-white text-sm gradient-bg glow-sm">Pay {fmtINR(price)} via Payment Link <ExternalLink className="h-4 w-4" /></a>
      : <p className="text-xs px-3 py-2 rounded-lg bg-amber-500/10 text-amber-600">Payment link not configured — contact support.</p>}
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Payment Reference / Transaction ID *"><Input data-testid={`${testidPrefix}-pay-ref`} value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} placeholder="e.g. 412345678901" /></Field>
      <Field label="Payment Date"><Input data-testid={`${testidPrefix}-pay-date`} type="date" value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} /></Field>
      <Field label="Paid Amount (₹)"><Input data-testid={`${testidPrefix}-pay-amount`} type="number" min="1" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} placeholder={String(price)} /></Field>
      <Field label="Payment Screenshot (optional)">
        <div className="flex items-center gap-2">
          <FileBtn testid={`${testidPrefix}-pay-screenshot`} label={pay.screenshot ? "Uploaded ✓" : "Upload"} onDone={(u) => setPay({ ...pay, screenshot: u })} />
          {pay.screenshot && <img src={fileUrlSafe(pay.screenshot)} alt="" className="h-9 w-9 rounded-lg object-cover" />}
        </div>
      </Field>
    </div>
  </>
);
const fileUrlSafe = (u) => (u?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

// ================= NEW PERSON (logged out) — basic details + payment only =================
function RegisterBasic() {
  const { setUser } = useAuth();
  const [cfg, setCfg] = useState({ subscription_price: 99, payment_link: "" });
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm: "", display_name: "", title: "", location: "" });
  const [pay, setPay] = useState({ reference: "", payment_date: "", amount: "", screenshot: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/mkt/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (done) return <PendingScreen />;
  const price = cfg.subscription_price;

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    if (!form.display_name.trim()) { toast.error("Display name is required"); return; }
    if (!pay.reference.trim()) { toast.error("Payment reference / transaction ID is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/mkt/freelancer/register", {
        name: form.name, email: form.email, phone: form.phone, password: form.password,
        display_name: form.display_name, title: form.title, location: form.location,
        reference: pay.reference, payment_date: pay.payment_date, amount: pay.amount || price, screenshot: pay.screenshot,
      });
      localStorage.setItem("zylo_token", data.token);
      setUser(data.user);
      toast.success("Application submitted for verification!");
      setDone(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <style>{MKT_CSS}</style>
      <MktHeader />
      <div className="max-w-2xl mx-auto px-4 py-10">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mkt-sec-title" data-testid="fl-reg-title">Join as Freelancer</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Just your <b>basic details + membership payment</b> ({fmtINR(price)}/month). After admin approval you'll complete your full profile, skills, portfolio and payout details from your dashboard.
        </p>

        <div className="space-y-6 mt-8">
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mkt-card p-6 space-y-4" data-testid="fl-reg-account">
            <p className="font-display font-bold flex items-center gap-2"><span className="h-6 w-6 rounded-full gradient-bg text-white text-xs font-extrabold flex items-center justify-center">1</span> Account Details <span className="text-[10px] font-bold text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-full">FREE account</span></p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name *"><Input data-testid="fr-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Email *"><Input data-testid="fr-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Mobile Number"><Input data-testid="fr-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Password *"><Input data-testid="fr-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
                <Field label="Confirm *"><Input data-testid="fr-confirm" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} /></Field>
              </div>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mkt-card p-6 space-y-4" data-testid="fl-reg-basic">
            <p className="font-display font-bold flex items-center gap-2"><span className="h-6 w-6 rounded-full gradient-bg text-white text-xs font-extrabold flex items-center justify-center">2</span> Basic Profile</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Display Name *" hint="Shown publicly on the marketplace"><Input data-testid="fr-display-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></Field>
              <Field label="Professional Title"><Input data-testid="fr-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. WordPress Developer" /></Field>
              <Field label="Location (optional)"><Input data-testid="fr-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mkt-card p-6 space-y-4" data-testid="fl-reg-payment">
            <p className="font-display font-bold flex items-center gap-2"><span className="h-6 w-6 rounded-full gradient-bg text-white text-xs font-extrabold flex items-center justify-center">3</span> Freelancer Membership · {fmtINR(price)}/month</p>
            <PayBlock link={cfg.payment_link} price={price} pay={pay} setPay={setPay} testidPrefix="fr" />
            <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500" />Your Freelancer account will be activated after payment verification and approval by the MILTROO admin. Manual verification — we never claim automatic confirmation.</p>
          </motion.section>

          <Button data-testid="fr-submit" onClick={submit} disabled={busy} className="w-full h-14 rounded-full font-bold gradient-bg text-white border-0 glow-sm text-base">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><UserPlus className="h-5 w-5 mr-2" />Pay & Submit Application</>}
          </Button>
          <p className="text-center text-xs text-muted-foreground">Already have a MILTROO account? <a href="/mkt/auth?return=/freelancer/apply" data-testid="fr-login-link" className="font-bold text-gradient">Sign in</a></p>
        </div>
      </div>
    </div>
  );
}

// ================= LOGGED-IN (customer / partner) — basic + payment =================
export default function FreelancerApply() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ display_name: "", title: "", location: "" });
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ reference: "", payment_date: "", amount: "", screenshot: "" });
  const navigate = useNavigate();

  const load = () => api.get("/mkt/freelancer/me").then((r) => {
    setMe(r.data);
    const f = r.data.freelancer || {};
    setForm({ display_name: f.display_name || "", title: f.title || "", location: f.location || "" });
    if (r.data.application_status === "draft" && r.data.freelancer?.display_name) setStep(2);
  }).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    if (user === undefined) return;
    if (!user) { setMe("guest"); return; }
    load();
  }, [user]);

  if (user === undefined || (user && !me)) return <div className="min-h-screen bg-background"><style>{MKT_CSS}</style><MktHeader /><div className="max-w-2xl mx-auto p-10"><LoadingRows rows={4} /></div></div>;
  if (!user) return <RegisterBasic />;
  if (me?.active) { navigate("/freelancer", { replace: true }); return null; }

  const status = me.application_status;
  const price = me.subscription_price;

  const submitApplication = async () => {
    if (!form.display_name.trim()) { toast.error("Display name is required"); return; }
    setBusy(true);
    try {
      await api.post("/mkt/freelancer/apply", form);
      toast.success("Basic details saved"); setStep(2); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitPayment = async () => {
    if (!pay.reference.trim()) { toast.error("Payment reference / transaction ID is required"); return; }
    setBusy(true);
    try {
      await api.post("/mkt/freelancer/apply/payment", { ...pay, amount: pay.amount || price });
      toast.success("Submitted for verification"); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (status === "pending") return <PendingScreen />;

  return (
    <div className="min-h-screen bg-background">
      <style>{MKT_CSS}</style>
      <MktHeader />
      <div className="max-w-2xl mx-auto px-4 py-10">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mkt-sec-title" data-testid="apply-title">Freelancer Application</h1>
        <p className="text-sm text-muted-foreground mt-2">Basic details + membership (<b>{fmtINR(price)}/month</b>) → admin approval → then complete your full profile from the dashboard.</p>

        {status === "rejected" && (
          <div data-testid="apply-rejected" className="mt-5 rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
            <div><p className="font-bold">Application rejected</p><p className="text-muted-foreground mt-0.5">{me.rejection_reason}</p><p className="text-xs mt-1">Correct the details below and resubmit.</p></div>
          </div>
        )}
        {status === "info_requested" && (
          <div data-testid="apply-info-requested" className="mt-5 rounded-2xl border border-blue-500/30 bg-blue-500/10 p-4 text-sm">
            <p className="font-bold">More information requested</p><p className="text-muted-foreground mt-0.5">{me.info_note}</p>
          </div>
        )}
        {me.role === "PARTNER" && (
          <p data-testid="apply-partner-note" className="mt-5 text-xs px-4 py-2.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-semibold inline-block">
            Existing Partner — same account will be used. No duplicate account, partner features untouched.
          </p>
        )}

        <div className="flex items-center gap-2 mt-8 mb-8">
          {["Basic Details", "Payment"].map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <span className={`h-8 w-8 rounded-full text-xs font-extrabold flex items-center justify-center shrink-0 ${step > i ? "gradient-bg text-white" : "bg-muted text-muted-foreground"}`}>{i + 1}</span>
              <span className={`text-xs font-bold ${step > i ? "" : "text-muted-foreground"}`}>{s}</span>
              {i < 1 && <div className="flex-1 h-px bg-border" />}
            </div>
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mkt-card p-6 sm:p-8">
          {step === 1 && (
            <div className="space-y-4" data-testid="apply-step-1">
              <Field label="Display Name *" hint="Shown publicly on the marketplace"><Input data-testid="ap-display-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></Field>
              <Field label="Professional Title"><Input data-testid="ap-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. WordPress Developer" /></Field>
              <Field label="Location (optional)"><Input data-testid="ap-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
              <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><BadgeCheck className="h-4 w-4 shrink-0 text-primary" />Skills, experience, portfolio, services and payout/bank details are collected after your account is approved.</p>
              <Button data-testid="ap-next-1" onClick={submitApplication} disabled={busy} className="w-full h-12 rounded-full font-bold gradient-bg text-white border-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to Payment <ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="apply-step-2">
              <div className="text-center space-y-1">
                <p className="overline-label">Freelancer Membership</p>
                <p className="font-display font-extrabold text-4xl">{fmtINR(price)}<span className="text-base text-muted-foreground"> / month</span></p>
              </div>
              <PayBlock link={me.payment_link} price={price} pay={pay} setPay={setPay} testidPrefix="ap" />
              <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500" />Your Freelancer account will be activated after payment verification and approval.</p>
              <Button data-testid="ap-pay-submit" onClick={submitPayment} disabled={busy} className="w-full h-12 rounded-full font-bold gradient-bg text-white border-0 glow-sm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pay & Submit Application"}
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
